import { useTranslation } from "react-i18next";
import { ListItem } from "@/components/common/ListItem";
import { UnreadBadgeStack } from "@/components/common/UnreadBadgeStack";
import { Button } from "@/components/ui/button";
import { ChatRoomAvatar } from "@/features/chat/components/ChatRoomAvatar";
import { ChatRoomParticipantsBadges } from "@/features/chat/components/ChatRoomParticipantsBadges";
import {
  buildUnreadBadgeItems,
  getUnreadCounts,
} from "@/features/chat/utils/unreadBadges";
import type { ChatRoomResponse } from "@/lib/humhub-api/types";

interface ChatRoomListProps {
  rooms: ChatRoomResponse[];
  onRoomClick: (roomId: number) => void;
  currentChatRoomId?: string | null;
  hasMore: boolean;
  onLoadMore: () => void;
  isLoadingMore: boolean;
  showParticipantsOnly?: boolean;
}

const buildChatRoomListItemProps = (
  room: ChatRoomResponse,
  showParticipantsOnly: boolean,
) => {
  const label = showParticipantsOnly ? (room.display_title ?? "") : room.title;
  const sublabel = showParticipantsOnly ? (
    (room.last_entry_content ?? "")
  ) : (
    <ChatRoomParticipantsBadges displayTitle={room.display_title} />
  );
  const avatarTitle = showParticipantsOnly
    ? (room.display_title ?? room.title)
    : room.title;

  return { label, sublabel, avatarTitle };
};

export const ChatRoomList: React.FC<ChatRoomListProps> = ({
  rooms,
  onRoomClick,
  currentChatRoomId,
  hasMore,
  onLoadMore,
  isLoadingMore,
  showParticipantsOnly = false,
}) => {
  const { t } = useTranslation();

  return (
    <div className="space-y-1 pr-1">
      {rooms.map((room) => {
        const isSelected = currentChatRoomId === String(room.id);
        const { unreadMessages, unreadMentions } = getUnreadCounts(room);
        const { label, sublabel, avatarTitle } = buildChatRoomListItemProps(
          room,
          showParticipantsOnly,
        );
        return (
          <ListItem
            key={room.id}
            icon={
              <ChatRoomAvatar
                title={room.title}
                displayTitle={avatarTitle}
                imageUrl={room.image_url}
                size={32}
              />
            }
            label={label}
            labelSingleLine
            sublabel={sublabel}
            className="px-2"
            onClick={() => onRoomClick(room.id)}
            selected={isSelected}
            selectedVariant="bar"
            density="compact"
            iconWrapperClassName="h-10 w-10"
            labelClassName="text-[16px] leading-[1.3] font-semibold"
            sublabelClassName="text-[12px] leading-[1.3] text-muted-foreground"
            rightContent={
              <UnreadBadgeStack
                items={buildUnreadBadgeItems(t, unreadMessages, unreadMentions)}
                align="end"
                className="pt-1"
              />
            }
          />
        );
      })}
      {hasMore && (
        <div className="p-2 text-center">
          <Button onClick={onLoadMore} disabled={isLoadingMore} variant="teal">
            {isLoadingMore ? t("common.loading") : t("common.showMore")}
          </Button>
        </div>
      )}
    </div>
  );
};
